import { useState } from 'react';
import { Users, UserPlus, X } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

// ─── Types ───────────────────────────────────────────────────────────────────

interface UpcomingPlayer {
  id: string;
  firstName: string;
  lastName: string;
  level: string | null;
}

interface PastPlayer {
  id: string;
  firstName: string;
  lastName: string;
  level: string | null;
  status: string;
  hasMakeupRequest: boolean;
  evaluation: {
    attitude: number;
    effort: number;
    engagement: number;
    physical: number | null;
    technical: number | null;
    tactical: number | null;
    competitionLevel: number;
    notes: string | null;
  } | null;
}

type PlayerItem = UpcomingPlayer | PastPlayer;

interface PlayerRosterProps {
  players: PlayerItem[];
  isPast: boolean;
  sessionId: string;
  onAddPlayers: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusColor(status: string) {
  switch (status) {
    case 'present': return 'border-l-green-500 bg-green-50 dark:bg-green-950/30';
    case 'late': return 'border-l-yellow-500 bg-yellow-50 dark:bg-yellow-950/30';
    case 'absent': return 'border-l-red-500 bg-red-50 dark:bg-red-950/30';
    case 'excused': return 'border-l-gray-400 bg-muted/50';
    default: return '';
  }
}

function statusBadgeVariant(status: string) {
  switch (status) {
    case 'present': return 'default' as const;
    case 'late': return 'secondary' as const;
    case 'absent': return 'destructive' as const;
    default: return 'outline' as const;
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function UpcomingPlayerRow({
  player,
  sessionId,
}: {
  player: UpcomingPlayer;
  sessionId: string;
}) {
  const removePlayer = useMutation(api.director.sessions.removePlayer);
  const [removing, setRemoving] = useState(false);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await removePlayer({
        sessionId: sessionId as Id<'trainingSessions'>,
        playerId: player.id as Id<'players'>,
      });
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg">
      <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 flex items-center justify-center text-sm font-medium">
        {player.firstName?.[0] ?? '?'}{player.lastName?.[0] ?? ''}
      </div>
      <div className="flex-1">
        <p className="font-medium text-foreground">
          {player.firstName} {player.lastName}
        </p>
      </div>
      {player.level && (
        <Badge variant="outline" className="capitalize">
          {player.level}
        </Badge>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="text-muted-foreground hover:text-red-500"
        disabled={removing}
        onClick={handleRemove}
      >
        <X className="w-4 h-4" />
      </Button>
    </div>
  );
}

function EvalGrid({
  evaluation,
}: {
  evaluation: NonNullable<PastPlayer['evaluation']>;
}) {
  const criteria = [
    { label: 'Attitude', value: evaluation.attitude },
    { label: 'Effort', value: evaluation.effort },
    { label: 'Engagement', value: evaluation.engagement },
    { label: 'Physical', value: evaluation.physical },
    { label: 'Technical', value: evaluation.technical },
    { label: 'Tactical', value: evaluation.tactical },
    { label: 'Competition', value: evaluation.competitionLevel },
  ];

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        {criteria.map(
          (c) =>
            c.value != null && (
              <div key={c.label} className="flex justify-between">
                <span className="text-muted-foreground">{c.label}</span>
                <span className="font-medium">{c.value}/4</span>
              </div>
            )
        )}
      </div>
      {evaluation.notes && (
        <p className="text-xs text-muted-foreground border-t pt-2 mt-2">
          {evaluation.notes}
        </p>
      )}
    </div>
  );
}

function PastPlayerRow({ player }: { player: PastPlayer }) {
  return (
    <div className={`border-l-4 rounded-lg border ${statusColor(player.status)}`}>
      <div className="flex items-center gap-3 p-3">
        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 flex items-center justify-center text-sm font-medium">
          {player.firstName?.[0] ?? '?'}{player.lastName?.[0] ?? ''}
        </div>
        <div className="flex-1">
          <p className="font-medium text-foreground">
            {player.firstName} {player.lastName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {player.level && (
            <Badge variant="outline" className="capitalize">
              {player.level}
            </Badge>
          )}
          <Badge variant={statusBadgeVariant(player.status)} className="capitalize">
            {player.status}
          </Badge>
          {player.hasMakeupRequest && (
            <Badge variant="outline" className="border-orange-400 text-orange-600">
              Makeup
            </Badge>
          )}
          {player.evaluation && (
            <details className="inline">
              <summary className="cursor-pointer">
                <Badge variant="outline" className="border-blue-400 text-blue-600">
                  Evaluated
                </Badge>
              </summary>
              <div className="absolute right-4 mt-1 bg-card border rounded-lg shadow-lg p-3 z-10 w-64">
                <EvalGrid evaluation={player.evaluation} />
              </div>
            </details>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function PlayerRoster({
  players,
  isPast,
  sessionId,
  onAddPlayers,
}: PlayerRosterProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            {isPast ? 'Attendance' : 'Roster'}
          </CardTitle>
          {!isPast && (
            <Button size="sm" onClick={onAddPlayers}>
              <UserPlus className="w-4 h-4 mr-2" />
              Add Players
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {players.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            {isPast
              ? 'No attendance records for this session.'
              : 'No players added yet. Use Add Players to build the roster.'}
          </div>
        ) : (
          <div className="space-y-2">
            {players.map((player) => (
              <div key={player.id}>
                {isPast ? (
                  <PastPlayerRow player={player as PastPlayer} />
                ) : (
                  <UpcomingPlayerRow
                    player={player as UpcomingPlayer}
                    sessionId={sessionId}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
